import React, { useRef, useEffect } from 'react';
import {
    Box,
    Toolbar,
    Typography,
    Paper,
    TextField,
    IconButton,
    Tabs,
    Tab,
    Divider,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Grid,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { useWorkingHourController } from './workingHourController';
import Overrides from './overridesDates';
import { format } from 'date-fns';
import TimezoneSelector from '../../util/timezoneSelector';
import {
    ConvertLocal12To24Hour,
    ConvertUTCToLocal12Hour,
    GetSortedWorkingHours
} from '../../util/workingHour';
import Holiday from './business/holiday';
import { EntityType } from '../../redux/actions/businessActions';
import { StyledBox } from '../../assets/css/styledComponent';

const TimeInputFields = React.memo(({
    startTime,
    endTime,
    hasError,
    onChangeStart,
    onChangeEnd,
    timeZone,
}) => {
    const formattedStartTime = ConvertLocal12To24Hour(startTime, timeZone);
    const formattedEndTime = ConvertLocal12To24Hour(endTime, timeZone);
    const [localStartTime, setLocalStartTime] = React.useState(formattedStartTime || "");
    const [localEndTime, setLocalEndTime] = React.useState(formattedEndTime || "");
    const pendingStartRef = React.useRef(null);
    const pendingEndRef = React.useRef(null);
    const startFocusedRef = React.useRef(false);
    const endFocusedRef = React.useRef(false);

    useEffect(() => {
        if (startFocusedRef.current) {
            return;
        }
        if (pendingStartRef.current) {
            const { value } = pendingStartRef.current;
            if (formattedStartTime !== value) {
                return;
            }
            if (formattedStartTime === value) {
                pendingStartRef.current = null;
            }
        }
        setLocalStartTime(formattedStartTime || "");
    }, [formattedStartTime]);

    useEffect(() => {
        if (endFocusedRef.current) {
            return;
        }
        if (pendingEndRef.current) {
            const { value } = pendingEndRef.current;
            if (formattedEndTime !== value) {
                return;
            }
            if (formattedEndTime === value) {
                pendingEndRef.current = null;
            }
        }
        setLocalEndTime(formattedEndTime || "");
    }, [formattedEndTime]);

    const commitStartTime = () => {
        if (localStartTime !== (formattedStartTime || "")) {
            pendingStartRef.current = { value: localStartTime };
            onChangeStart({ target: { value: localStartTime } });
        }
    };

    const commitEndTime = () => {
        if (localEndTime !== (formattedEndTime || "")) {
            pendingEndRef.current = { value: localEndTime };
            onChangeEnd({ target: { value: localEndTime } });
        }
    };

    return (
        <Box display="flex" alignItems="center">
            <TextField
                type="time"
                value={localStartTime}
                onChange={(e) => setLocalStartTime(e.target.value)}
                onFocus={() => {
                    startFocusedRef.current = true;
                }}
                onBlur={() => {
                    startFocusedRef.current = false;
                    commitStartTime();
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.currentTarget.blur();
                    }
                }}
                error={hasError}
                helperText={hasError ? "Invalid time range" : ""}
                fullWidth
                InputProps={{ sx: { maxWidth: '120px' } }}
                sx={{
                    borderRadius: '8px',
                    '& .MuiInputBase-root': { color: '#333' },
                    '& .Mui-error': { color: 'red' },
                    '& .Mui-focused': { borderColor: '#4caf50' },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc', borderRadius: '8px' },
                        '&:hover fieldset': { borderColor: '#888' },
                        '&.Mui-focused fieldset': { borderColor: '#4caf50' }
                    }
                }}
            />
            <Box sx={{ mx: 1 }}> - </Box>
            <TextField
                type="time"
                value={localEndTime}
                onChange={(e) => setLocalEndTime(e.target.value)}
                onFocus={() => {
                    endFocusedRef.current = true;
                }}
                onBlur={() => {
                    endFocusedRef.current = false;
                    commitEndTime();
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.currentTarget.blur();
                    }
                }}
                error={hasError}
                helperText={hasError ? "Invalid time range" : ""}
                fullWidth
                InputProps={{ sx: { maxWidth: '120px' } }}
                sx={{
                    borderRadius: '8px',
                    '& .MuiInputBase-root': { color: '#333' },
                    '& .Mui-error': { color: 'red' },
                    '& .Mui-focused': { borderColor: '#4caf50' },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc', borderRadius: '8px' },
                        '&:hover fieldset': { borderColor: '#888' },
                        '&.Mui-focused fieldset': { borderColor: '#4caf50' }
                    }
                }}
            />
        </Box>
    );
});

const WorkingHourView = (props) => {
    const {
        workingHourGroup,
        hasErrors,
        handleChange,
        timeRangeErrors,
        handleAddRow,
        handleDeleteRow,
        copyWorkingHoursToDays,
        getWorkingHours,
        getWorkingHourOverrides,
        getWorkingHourHolidays,
        handleTimezoneSelect,
        workingHourOverrideGroups,
        refetchOverrides,
        handleDeleteWorkingHourGroup
    } = useWorkingHourController(props);

    // Sorted working hours based on the group's timezone
    const sortedWorkingHours = GetSortedWorkingHours(
        workingHourGroup?.workingHours || [],
        workingHourGroup?.timeZone
    );
    const overridesRef = useRef(null);
    const [tabValue, setTabValue] = React.useState(0);

    // States for copy row dialog functionality
    const [copyDialogOpen, setCopyDialogOpen] = React.useState(false);
    const [rowToCopy, setRowToCopy] = React.useState(null);
    const [selectedCopyDay, setSelectedCopyDay] = React.useState('');

    // Handle tab changes and fetch corresponding data
    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        if (tabValue === 0) {
            getWorkingHours();
        } else if (tabValue === 1) {
            getWorkingHourOverrides();
        } else if (tabValue === 2) {
            getWorkingHourHolidays();
        }
    }, [tabValue]);

    // Function to open the copy row dialog and store row data to copy
    const openCopyDialog = (parent, children) => {
        setRowToCopy({ parent, children });
        setCopyDialogOpen(true);
    };
    const cleanWorkingHour = (record) => {
        const toIntOrNull = (val) => {
            if (val === null || val === undefined || val === "") return null;
            const n = Number(val);
            return Number.isFinite(n) ? n : null;
        };
        const { __typename, ...rest } = record;
        let cleaned = { ...rest };
        cleaned.id = toIntOrNull(cleaned.id);
        cleaned.groupId = toIntOrNull(cleaned.groupId);
        cleaned.parentId = toIntOrNull(cleaned.parentId);
        return cleaned;
    };

    // Function to handle copy confirmation.
    // For each target day:
    // - If a row exists for that day, update its fields using the copied parent data,
    //   preserving its existing day and id.
    // - Replace its children with copies of the source children, setting their parentId to the target parent's id.
    // - If no row exists, add a new row as before.
    const handleCopyRowConfirm = () => {
        if (!rowToCopy || !selectedCopyDay) return;

        const normalizeDay = (day) => String(day || "").trim().toLowerCase();
        const canonicalDay = (day) => {
            const map = {
                mon: "Monday",
                monday: "Monday",
                tue: "Tuesday",
                tues: "Tuesday",
                tuesday: "Tuesday",
                wed: "Wednesday",
                weds: "Wednesday",
                wednesday: "Wednesday",
                thu: "Thursday",
                thur: "Thursday",
                thurs: "Thursday",
                thursday: "Thursday",
                fri: "Friday",
                friday: "Friday",
                sat: "Saturday",
                saturday: "Saturday",
                sun: "Sunday",
                sunday: "Sunday",
                all: "All",
            };
            return map[normalizeDay(day)] || String(day || "").trim();
        };

        const selected = canonicalDay(selectedCopyDay);
        const sourceParent = cleanWorkingHour(rowToCopy.parent);
        const sourceDay = canonicalDay(sourceParent.day);
        const targetDays =
            selected === "All"
                ? ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
                : [selected];
        const daysToApply = targetDays.filter((d) => d !== sourceDay);
        copyWorkingHoursToDays(
            sourceParent.id,
            daysToApply,
            workingHourGroup?.timeZone,
            { parent: rowToCopy.parent, children: rowToCopy.children }
        );

        setCopyDialogOpen(false);
        setSelectedCopyDay("");
        setRowToCopy(null);
    };

    // Component to render each row (parent and its child rows)
    const TimeRowData = ({ parent, children }) => {
        const { day, startTime, endTime, onJob, id } = parent;
        if (!onJob && day === "") return null;

        return (
            <>
                {/* Parent Row */}
                <StyledBox display="flex" alignItems="center" marginBottom={2} flexWrap={onJob ? 'wrap' : 'nowrap'} >
                    <StyledBox width="7%" minWidth="40px" marginBottom={1}>
                        {day && (
                            <Checkbox
                                checked={onJob}
                                onChange={(e) =>
                                    handleChange(parent, children, parent, e, "onJob", workingHourGroup?.timeZone)
                                }
                            />
                        )}
                    </StyledBox>
                    <StyledBox width="5%" minWidth="80px" marginBottom={1}>
                        {day && <Typography fontWeight="bold">{day?.slice(0, 3).toUpperCase()}</Typography>}
                    </StyledBox>
                    <StyledBox
                        width="80%"
                        minWidth="20px"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {onJob ? (
                            <StyledBox display="flex" alignItems="center" gap={2} width="100%">
                                <TimeInputFields
                                    startTime={startTime}
                                    endTime={endTime}
                                    timeZone={workingHourGroup?.timeZone}
                                    hasError={timeRangeErrors.find(err => err.id === parent.id)?.message}
                                    onChangeStart={(e) =>
                                        handleChange(parent, children, parent, e, "startTime", workingHourGroup?.timeZone)
                                    }
                                    onChangeEnd={(e) =>
                                        handleChange(parent, children, parent, e, "endTime", workingHourGroup?.timeZone)
                                    }
                                />
                                {/* Add Row Button */}
                                <IconButton
                                    size="small"
                                    onClick={() => handleAddRow(parent, children, workingHourGroup?.timeZone)}
                                    aria-label="add"
                                >
                                    <AddIcon />
                                </IconButton>
                                {/* Copy Row Button */}
                                <IconButton
                                    size="small"
                                    onClick={() => openCopyDialog(parent, children)}
                                    aria-label="copy"
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            </StyledBox>
                        ) : (
                            <Typography color="textSecondary" style={{ opacity: 0.5 }}>
                                Off Work
                            </Typography>
                        )}
                    </StyledBox>
                </StyledBox>

                {/* Child Rows */}
                {onJob &&
                    children.map((child, childIndex) => (
                        <StyledBox
                            display="flex"
                            alignItems="center"
                            marginBottom={2}
                            flexWrap="wrap"
                            key={child?.id ?? `${id}-child-${childIndex}`}
                        >
                            <StyledBox width="7%" minWidth="40px" marginBottom={1} />
                            <StyledBox width="5%" minWidth="80px" marginBottom={1} />
                            <StyledBox
                                width="80%"
                                minWidth="20px"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <StyledBox display="flex" alignItems="center" gap={2} width="100%">
                                    <TimeInputFields
                                        startTime={child.startTime}
                                        endTime={child.endTime}
                                        timeZone={workingHourGroup?.timeZone}
                                        hasError={timeRangeErrors.find(err => err.id === child.id || err.id === "new")?.message}
                                        onChangeStart={(e) =>
                                            handleChange(parent, children, child, e, "startTime", workingHourGroup?.timeZone)
                                        }
                                        onChangeEnd={(e) =>
                                            handleChange(parent, children, child, e, "endTime", workingHourGroup?.timeZone)
                                        }
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteRow(child, parent.groupId)}
                                        aria-label="delete"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </StyledBox>
                                {hasErrors && (
                                    <Typography color="error">
                                        {timeRangeErrors.find(err => err.id === child.id || err.id === "new")?.message}
                                    </Typography>
                                )}
                            </StyledBox>
                        </StyledBox>
                    ))}
                <Divider sx={{ my: 2 }} />
            </>
        );
    };

    // Format date helper function
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            if (typeof dateString === 'number' || /^\d+$/.test(dateString)) {
                return format(new Date(parseInt(dateString)), 'MMM dd, yyyy');
            }
            return format(new Date(dateString), 'MMM dd, yyyy');
        } catch (error) {
            console.error('Error formatting date:', error, dateString);
            return 'Invalid Date';
        }
    };

    // Tab configuration for the various sections
    const tabConfig = [
        {
            label: 'Weekly Hours',
            show: true,
            content: (
                <StyledBox sx={{ marginTop: '15px' }}>
                    {/* Timezone Selector */}
                    <Box sx={{ borderRadius: 1 }}>
                        <Grid alignItems="center">
                            <Grid container alignItems="center" spacing={0}>
                                <Grid item xs>
                                    <FormControl fullWidth>
                                        <TimezoneSelector
                                            timezoneId={workingHourGroup?.timeZone?.id}
                                            onTimezoneSelect={handleTimezoneSelect}
                                            isDisabled={props.isDisabled}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                    {sortedWorkingHours?.map(({ parent, children }) => (
                        <TimeRowData key={parent.id} parent={parent} children={children} />
                    ))}
                </StyledBox>
            )
        },
        {
            label: 'Overrides',
            show: props.entityType === EntityType.STAFF && !props.hideOverride,
            content: (
                <StyledBox sx={{ p: 2 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Define specific dates that you want to override and be available on.
                    </Typography>
                    <Overrides
                        ref={overridesRef}
                        staffData={props.staffData}
                        refetchOverrides={refetchOverrides}
                        providerUser={props.providerUser}
                    />
                    {workingHourOverrideGroups && workingHourOverrideGroups?.getWorkingHourOverrideGroups?.length > 0 ? (
                        <TableContainer component={Paper} sx={{ mt: 3 }}>
                            <Table aria-label="time off periods">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Start Date  - End Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {workingHourOverrideGroups.getWorkingHourOverrideGroups.map((group) => (
                                        <TableRow key={group.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>{group.title}</TableCell>
                                            <TableCell>
                                                <div>
                                                    {formatDate(group.startDate)} - {formatDate(group.endDate)}
                                                </div>
                                                {group.workingHourOverrides.map((override, index) => {
                                                    const displayStartTime =
                                                        override.startTime.trim().toLowerCase() === 'all day'
                                                            ? '- All day off work'
                                                            : ConvertUTCToLocal12Hour(override.startTime, props.providerUser?.timeZone);
                                                    const displayEndTime =
                                                        override.endTime.trim().toLowerCase() === 'all day'
                                                            ? ''
                                                            : ConvertUTCToLocal12Hour(override.endTime, props.providerUser?.timeZone);
                                                    return (
                                                        <div key={index}>
                                                            {displayStartTime} - {displayEndTime}
                                                        </div>
                                                    );
                                                })}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => overridesRef.current?.handleOpen(group)}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton onClick={() => handleDeleteWorkingHourGroup(group.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
                            No overrides defined
                        </Typography>
                    )}
                </StyledBox>
            )
        },
        {
            label: 'Holidays',
            show: props.entityType === EntityType.BUSINESS,
            content: (
                <StyledBox sx={{ p: 2 }}>
                    <Holiday />
                </StyledBox>
            )
        }
    ];

    // Filter out hidden tabs while preserving order
    const visibleTabs = tabConfig.filter(tab => tab.show);

    return (
        <StyledBox
            sx={{
                maxWidth: '800px', 
                opacity: props.isDisabled ? 0.6 : 1,
          }}
        >
          <Toolbar>
                <Typography variant="h6">
                    Set your {props.isCustom ? "custom" : "default"} availability schedule
                </Typography>
            </Toolbar>
          <StyledBox elevation={0} sx={{ padding: 2 }}>
                <>
                    <Tabs value={tabValue} onChange={handleChangeTab} aria-label="business working hours tabs">
                        {visibleTabs?.map((tab, index) => (
                            <Tab key={index} label={tab.label} />
                        ))}
                    </Tabs>
                    {/* Render content for the active tab */}
                    <Box>{visibleTabs[tabValue]?.content}</Box>
                </>
          </StyledBox>

            {/* Copy Row Dialog */}
            <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)}>
                <DialogTitle>Copy Working Hours Row</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Select the day to which you want to update (replace) the existing row with the copied row. Choose "All Days" to update for every day.
                    </DialogContentText>
                    <TextField
                        select
                        label="Day"
                        fullWidth
                        value={selectedCopyDay}
                        onChange={(e) => setSelectedCopyDay(e.target.value)}
                        SelectProps={{ native: true }}
                        variant="outlined"
                        sx={{ mt: 2 }}
                    >
                        <option value="">Select a day</option>
                        <option value="All">All Days</option>
                        <option value="Monday">Monday</option>
                        <option value="Tuesday">Tuesday</option>
                        <option value="Wednesday">Wednesday</option>
                        <option value="Thursday">Thursday</option>
                        <option value="Friday">Friday</option>
                        <option value="Saturday">Saturday</option>
                        <option value="Sunday">Sunday</option>
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCopyRowConfirm} disabled={!selectedCopyDay}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledBox>
    );
};

export default WorkingHourView;


